import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DataHeader } from 'private/app/models/account-admin-search';
import { Selectable, SelectablePaginationUtils } from '../../../../../common/utils/PaginationUtils';
import * as XLSX from 'xlsx';
import { ToastService } from 'common/services/toast.service';
import { UserRegistrationRequest } from 'private/app/models/user.model';

interface ApprovalRequestUserExportFormat {
    'First Name': string;
    'Last Name': string;
    'User ID': string;
    'Company Name': string;
    'Company ID': string;
    'Created Date': string;
    'Company Type Name': string;
    'Owner Company Name': string;
    'User Email': string;
    Status: string;
}

@Component({
    selector: 'hvac-user-registrations-results',
    templateUrl: './user-registrations-results.component.html',
    styleUrls: ['./user-registrations-results.component.scss']
})
export class UserRegistrationsResultsComponent implements OnInit, OnChanges {
    @Input() users: UserRegistrationRequest[] = [];
    @Input() loading: boolean;
    @Output() registrationUserChangePage = new EventEmitter<{
        registrationUserStep: string,
        usersData : UserRegistrationRequest
    }>();

    public dataHeaders: DataHeader[] = [
        {
            title: this.translateService.instant('ACCOUNT_ADMIN.FIRST_NAME'),
            value: 'firstName',
            order: null
        },
        {
            title: this.translateService.instant('ACCOUNT_ADMIN.LAST_NAME'),
            value: 'lastName',
            order: null
        },
        {
            title: this.translateService.instant('ACCOUNT_ADMIN.USER_ID'),
            value: 'userName',
            order: null
        },
        {
            title: this.translateService.instant('ACCOUNT_ADMIN.OWNER_COMPANY_NAME'),
            value: 'ownerCompanyName',
            order: null
        },
        {
            title: this.translateService.instant('ACCOUNT_ADMIN.COMPANY_NAME'),
            value: 'companyName',
            order: null
        },
        {
            title: this.translateService.instant('ACCOUNT_ADMIN.COMPANY_ID'),
            value: 'companyId',
            order: null
        },
        {
            title: this.translateService.instant('ACCOUNT_ADMIN.CREATED_DATE'),
            value: 'createdDate',
            order: null
        },
        {
            title: this.translateService.instant('ACCOUNT_ADMIN.STATUS'),
            value: 'userStatus',
            order: null
        },
        {
            title: '',
            value: '',
            order: null
        },
        {
            title: '',
            value: '',
            order: null
        }
    ];

    public readonly pageSize = 10;
    public readonly ownerCompanyNamePreviewLength = 30;
    public usersData: SelectablePaginationUtils<UserRegistrationRequest>;
    public isQuickViewDrawerOpen = false;
    public selectedQuickViewUser: UserRegistrationRequest | null = null;

    constructor(
        private readonly translateService: TranslateService,
        private readonly toastService: ToastService
    ) {
    }

    ngOnInit(): void {
        this.usersData = new SelectablePaginationUtils(this.users.map(this.transformUserDataToSelectableData), this.pageSize, this.dataHeaders);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.users) {
            const users: UserRegistrationRequest[] = changes.users.currentValue;

            this.closeQuickViewDrawer();
            this.usersData?.initData(users.map(this.transformUserDataToSelectableData));
        }
    }

    sort(item: DataHeader): void {
        this.usersData.sort(item);
    }

    onPageChange(pageNum: number): void {
        this.closeQuickViewDrawer();
        this.usersData.onPageChange(pageNum);
    }

    openQuickViewDrawer(user: UserRegistrationRequest): void {
        this.selectedQuickViewUser = user;
        this.isQuickViewDrawerOpen = true;
    }

    closeQuickViewDrawer(): void {
        this.isQuickViewDrawerOpen = false;
        this.selectedQuickViewUser = null;
    }

    onQuickViewReviewClick(): void {
        if (!this.selectedQuickViewUser) {
            return;
        }

        const user = this.selectedQuickViewUser;

        this.closeQuickViewDrawer();
        this.onClickReview(user);
    }

    selectUserRegistration(event: Event, selectedId: string) {
        this.usersData.selectItem(event, selectedId);
    }

    onSelectAllFromAllPagesClick() {
        this.usersData.onSelectAllFromAllPagesClick();
    }

    onExportSelectedClick() {
        const selectedUsers = this.usersData.getAllPagesData.filter((data) => data.isSelected).map((filteredData) => filteredData.data);

        if (selectedUsers && selectedUsers.length === 0) {
            this.toastService.add({
                content: this.translateService.instant('ACCOUNT_ADMIN.USER_REGISTRATIONS_PAGE.ERRORS.NO_RESULTS_SELECTED_TO_EXPORT'),
                autoClose: true,
                closeable: true,
                theme: 'error',
                id: this.translateService.instant('ACCOUNT_ADMIN.USER_REGISTRATIONS_PAGE.TOAST_ID')
            });

            return;
        }

        const title = `User_Registrations_${new Date().toDateString()}`;
        const fileName = `${title}.xlsx`;
        const workSheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.transformToExportUserFormart(selectedUsers));
        const workBook: XLSX.WorkBook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workBook, workSheet, 'Sheet1');
        XLSX.writeFile(workBook, fileName);
    }

    public onClickReview(usersData: UserRegistrationRequest) {
        this.registrationUserChangePage.emit({
            registrationUserStep: 'step1_userRegistrationForm',
            usersData: usersData
        });
    }

    clearSelectAllCheck() {
        this.usersData.isSelectAllChecked = false;
        this.usersData.onDeselectAllFromAllPagesClick();
    }

    getOwnerCompanyNamePreview(name: string | null | undefined): string {
        if (!name) {
            return '';
        }

        if (name.length <= this.ownerCompanyNamePreviewLength) {
            return name;
        }

        return `${name.substring(0, this.ownerCompanyNamePreviewLength).trim()}...`;
    }

    private transformToExportUserFormart(users: UserRegistrationRequest[]): ApprovalRequestUserExportFormat[] {
        return users.map((user): ApprovalRequestUserExportFormat => ({
            'First Name': user.firstName,
            'Last Name': user.lastName,
            'User ID': user.userName || '',
            'Company Name': user.companyName,
            'Company ID': user.companyId,
            'Company Type Name': user.companyType,
            'Owner Company Name': user.ownerCompanyName,
            'User Email': user.email,
            'Created Date': new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }).format(new Date(user.createdDate)),
            'Status': user.userStatus || ''
        }));
    }

    private transformUserDataToSelectableData = (userData: UserRegistrationRequest): Selectable<UserRegistrationRequest> => ({
        data: userData,
        isSelected: false,
        getId: () => userData.firstName + userData.lastName + userData.email + userData.companyId
    })
}
